import moment from "moment";
import { NotificationFilters, NotificationItem } from "../Repositories/NotificationRepository";
import { getActiveClientBrandConfig } from "../config/environment.config";

/**
 * Helpers purs du centre de notifications (panneau, page, widget) : libellés des
 * catégories, filtre local et regroupement par ancienneté. Aucun appel réseau,
 * aucun état : testés dans src/Test/Notifications.
 */

export type NotificationGroupKey = "today" | "week" | "older";

export interface NotificationGroup {
    key: NotificationGroupKey;
    /** En-tête affiché au-dessus du groupe. */
    label: string;
    items: NotificationItem[];
}

/** Ordre d'affichage des groupes et leurs en-têtes. */
export const NOTIFICATION_GROUP_LABELS: Record<NotificationGroupKey, string> = {
    today: "Aujourd'hui",
    week: "Cette semaine",
    older: "Plus ancien",
};

const GROUP_ORDER: NotificationGroupKey[] = ["today", "week", "older"];

/** Nombre de jours (hors aujourd'hui) couverts par « Cette semaine ». */
export const WEEK_GROUP_DAYS = 7;

/** Ordre des puces de filtre par catégorie (une catégorie inconnue passe en dernier). */
export const NOTIFICATION_CATEGORY_ORDER = ["declaration", "travel", "billing", "client", "account"];

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/** Libellé de la puce d'une catégorie ; le vocabulaire client suit la marque active. */
export const notificationCategoryLabel = (category: string): string => {
    switch (category) {
        case "declaration":
            return "Déclarations";
        case "travel":
            return "Déplacements";
        case "billing":
            return "Facturation";
        case "client":
            return capitalize(getActiveClientBrandConfig("s"));
        case "account":
            return "Compte";
        default:
            return capitalize(category);
    }
};

/** Catégories présentes, dédoublonnées et dans l'ordre d'affichage. */
export const sortNotificationCategories = (categories: Iterable<string>): string[] => {
    const unique = Array.from(new Set(Array.from(categories).filter((category) => typeof category === "string" && category !== "")));
    const rank = (category: string) => {
        const index = NOTIFICATION_CATEGORY_ORDER.indexOf(category);
        return index === -1 ? NOTIFICATION_CATEGORY_ORDER.length : index;
    };
    return unique.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};

/** Groupe d'une notification selon sa date de création, relativement à `now`. */
export const notificationGroupKey = (createdAt: string, now: moment.MomentInput = undefined): NotificationGroupKey => {
    const reference = moment(now);
    // createdAt est en ISO 8601 (DATE_ATOM côté serveur) : analyse stricte, sans repli approximatif
    const created = moment(createdAt, moment.ISO_8601);
    // date illisible : on la range avec les plus anciennes plutôt que de la perdre
    if (!created.isValid()) return "older";
    // une date dans le futur (horloge en avance) reste dans « Aujourd'hui »
    if (created.isSameOrAfter(reference, "day")) return "today";
    if (created.isAfter(reference.clone().startOf("day").subtract(WEEK_GROUP_DAYS, "days"))) return "week";
    return "older";
};

/**
 * Regroupe les notifications par ancienneté en conservant leur ordre ; seuls les
 * groupes non vides sont renvoyés.
 */
export const groupNotifications = (items: NotificationItem[], now: moment.MomentInput = undefined): NotificationGroup[] => {
    const buckets: Record<NotificationGroupKey, NotificationItem[]> = { today: [], week: [], older: [] };
    items.forEach((item) => {
        buckets[notificationGroupKey(item.createdAt, now)].push(item);
    });
    return GROUP_ORDER
        .filter((key) => buckets[key].length > 0)
        .map((key) => ({ key, label: NOTIFICATION_GROUP_LABELS[key], items: buckets[key] }));
};

/**
 * Filtre local, même règle que le serveur : catégorie exacte et/ou non lues
 * seulement. Sert au widget et au rendu optimiste entre deux chargements.
 */
export const filterNotifications = (items: NotificationItem[], filters: Partial<NotificationFilters> | null | undefined): NotificationItem[] => {
    if (!filters) return items;
    return items.filter((item) => (
        (!filters.category || item.category === filters.category)
        && (!filters.unreadOnly || !item.isRead)
    ));
};

/** Clé stable d'un jeu de filtres (limiteur de fréquence, cache). */
export const notificationFiltersKey = (filters: Partial<NotificationFilters> | null | undefined): string => (
    `${filters?.category ?? ""}|${filters?.unreadOnly ? "unread" : "all"}`
);
